// ─── Details: Elegant Columns ─────────────────────────────────
// 4-column card grid with icons. Dark-friendly, elegant layout.
// Uses standard event-details data: date, time, venue, address, note.

import React from 'react';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';

const DETAIL_ITEMS = [
  { key: 'date', label: 'Date', Icon: Calendar },
  { key: 'time', label: 'Time', Icon: Clock },
  { key: 'venue', label: 'Venue', Icon: MapPin },
  { key: 'address', label: 'Location', Icon: MapPin },
];

export default function DetailsElegantColumns({ section, data, theme }) {
  const anim = section.animation || {};
  const items = DETAIL_ITEMS.filter(i => data[i.key]).map(i => ({
    ...i,
    value: data[i.key],
  }));

  if (items.length === 0) return null;

  return (
    <section className="px-6 py-20 sm:py-24" data-testid="event-details-section">
      <div className="mx-auto max-w-6xl">
        <AnimatedSection preset={anim.section || 'fade-in'} className="mb-12 text-center">
          <p
            className="text-[11px] uppercase tracking-[0.2em] mb-3"
            style={{ color: theme.accent, fontFamily: theme.eyebrowFont }}
          >
            Information
          </p>
          <h2
            className="text-4xl sm:text-5xl"
            style={{ fontFamily: theme.headingFont, color: theme.textPrimary }}
          >
            Event Details
          </h2>
        </AnimatedSection>

        <div className={`grid gap-4 ${items.length >= 4 ? 'md:grid-cols-2 xl:grid-cols-4' : items.length >= 2 ? 'md:grid-cols-2' : ''}`}>
          {items.map((item, i) => (
            <AnimatedSection key={item.key} preset={anim.items || 'stagger-cards'} delay={i * 0.08}>
              <div
                className="rounded-[1.6rem] border p-6"
                style={{ backgroundColor: theme.surface, borderColor: theme.border }}
              >
                <div className="mb-4">
                  <item.Icon className="h-4 w-4" style={{ color: theme.accent }} />
                </div>
                <p
                  className="text-[11px] uppercase tracking-[0.16em]"
                  style={{ color: theme.textSecondary, fontFamily: theme.eyebrowFont }}
                >
                  {item.label}
                </p>
                <p className="mt-3 text-lg leading-7" style={{ color: theme.textPrimary }}>
                  {item.value}
                </p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {data.note && (
          <AnimatedSection preset="fade-in" delay={0.3}>
            <p className="mx-auto mt-6 max-w-3xl text-center leading-7" style={{ color: theme.textSecondary }}>
              {data.note}
            </p>
          </AnimatedSection>
        )}
      </div>
    </section>
  );
}
